import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, Mic, MicOff, PhoneOff, Radio, Video, VideoOff } from 'lucide-react';
import { useGuestSession, type GuestSession } from '@/hooks/useGuestSession';
import { cn } from '@/lib/utils';

// Public guest page (no login): an admitted guest publishes camera + mic to
// the participant gateway over RIFT-WebTransport and sees/hears the return
// feed on the same session (JSS#749, ADR-JSS-0007). From admission until
// Leave the guest stays connected whether or not the producer has put them on
// a slot; the page says which: "You're live" on a slot, waiting otherwise
// (ADR-JSS-0012, contracts v2 §6). Guests reach this page from the link a
// producer shares, /join/:slug; it loads the event from the guest lookup
// (/api/events/slug/:slug/guest), which answers for a private event too
// (#1175). The audience's /event/:slug viewer is a separate public page.

const CONNECTION_LABEL: Record<string, string> = {
  idle: 'Not connected',
  connecting: 'Connecting…',
  live: 'Connected',
  reconnecting: 'Reconnecting…',
  ended: 'Disconnected',
};

/** Where the frames go between the camera and the session, for the fps readout's tooltip. */
function fpsDetail(s: NonNullable<GuestSession['stats']>): string {
  return (
    `Camera ${s.captureFps} · encoded ${s.encodedFps} · sent ${s.sentFps} fps (of ${s.encoderFps}); ` +
    `${s.droppedSends} sends dropped, ${s.withheldFrames} frames held for a keyframe`
  );
}

function Preview({ stream, mirrored }: { stream: MediaStream | null; mirrored: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream;
  }, [stream]);
  return (
    <video
      ref={ref}
      autoPlay
      muted
      playsInline
      className={cn('w-full h-full object-cover bg-jeket-black-elevated', mirrored && '-scale-x-100')}
    />
  );
}

function RecordingNotice({ session, blocking }: { session: GuestSession; blocking: boolean }) {
  return (
    <div role="alert" className="rounded-lg border border-jeket-gold/40 bg-jeket-gold/10 p-4 space-y-3">
      <div className="flex items-center gap-2 font-semibold text-jeket-gold">
        <Radio size={16} /> This event records participants
      </div>
      <p className="text-sm text-jeket-silver-bright">
        Your camera and microphone will be recorded separately while you are on air, alongside the programme.
      </p>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={session.join.noticeAcknowledged}
          onChange={() => !session.join.noticeAcknowledged && session.acknowledgeNotice()}
          disabled={session.join.noticeAcknowledged}
        />
        I understand{blocking ? ' — join now' : ''}
      </label>
    </div>
  );
}

export default function GuestJoin() {
  const { slug = '' } = useParams<{ slug: string }>();
  const session = useGuestSession(slug);
  const { caps, join, connection, stats } = session;
  const [displayName, setDisplayName] = useState('');

  if (join.phase === 'loading' || caps === null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-jeket-black">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jeket-gold" />
      </div>
    );
  }

  if (join.phase === 'error' || !join.event) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-jeket-black">
        <p className="text-jeket-silver-muted">{join.error ?? 'Event not found'}</p>
      </div>
    );
  }

  const event = join.event;
  const admitted = join.phase === 'admitted';
  // On a slot: what the guest sends is what the site puts in the show.
  const live = admitted && join.slot !== null;
  const canJoin =
    caps.supported &&
    !!session.preview &&
    displayName.trim().length > 0 &&
    (!event.recordParticipantIsos || join.noticeAcknowledged);

  return (
    <div className="min-h-screen bg-jeket-black text-jeket-silver-bright flex flex-col">
      <header className="bg-jeket-black-surface border-b border-jeket-gold/20 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Jeket" className="h-6" />
          <h1 className="text-lg font-semibold">{event.name}</h1>
        </div>
        {admitted && (
          <div className="flex items-center gap-2">
            <span
              data-testid="slot-state"
              className={cn(
                'px-2 py-0.5 text-xs font-semibold rounded',
                live ? 'bg-red-500/20 text-red-400' : 'bg-jeket-silver-muted/20 text-jeket-silver-muted',
              )}
            >
              {live ? "You're live" : 'Waiting to go on'}
            </span>
            <span
              data-testid="connection-state"
              title={connection === 'live' && stats ? fpsDetail(stats) : undefined}
              className={cn(
                'px-2 py-0.5 text-xs font-medium rounded',
                connection === 'live' ? 'bg-jeket-healthy/20 text-jeket-healthy' : 'bg-jeket-gold/20 text-jeket-gold',
              )}
            >
              {CONNECTION_LABEL[connection]}
              {connection === 'live' && stats?.rttMs != null ? ` · ${stats.rttMs} ms` : ''}
              {connection === 'live' && stats ? ` · ${Math.round(stats.sentFps)} fps` : ''}
            </span>
          </div>
        )}
      </header>

      <main className="flex-1 p-6 max-w-5xl w-full mx-auto space-y-4">
        {!caps.supported && (
          <div role="alert" className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm">
            <p className="font-semibold">This browser can't join as a guest.</p>
            <p className="mt-1 text-jeket-silver-muted">
              Use a current Chrome or Edge on desktop. Missing: {caps.missing.join(', ')}.
            </p>
          </div>
        )}

        {caps.supported && !caps.aacEncode && (
          <div role="status" className="rounded-lg border border-jeket-gold/40 bg-jeket-gold/10 p-3 text-sm flex gap-2">
            <AlertTriangle size={16} className="text-jeket-gold shrink-0 mt-0.5" />
            <span>
              This browser can't encode AAC audio, so you'll be seen but not heard. Chrome or Edge on Windows or macOS
              can send audio.
            </span>
          </div>
        )}

        {session.mediaError && (
          <div role="alert" className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm">
            {session.mediaError}
          </div>
        )}

        {admitted && (
          <div
            role="status"
            data-testid="slot-banner"
            className={cn(
              'rounded-lg border p-3 text-sm',
              live ? 'border-red-500/40 bg-red-500/10 text-red-300' : 'border-jeket-gold/20 bg-jeket-black-surface',
            )}
          >
            {live ? (
              <>
                <span className="font-semibold">You're live.</span> The producer has put you on air.
              </>
            ) : (
              <>
                <span className="font-semibold">You're in, and waiting to go on.</span> The producer can see you and
                will bring you on air. Stay on this page.
              </>
            )}
          </div>
        )}

        <div className={cn('grid gap-4', admitted ? 'md:grid-cols-2' : 'md:grid-cols-[2fr_1fr]')}>
          {/* Return feed: rendered always so the player has its canvas the moment the guest is admitted. */}
          <section className={cn('space-y-2', !admitted && 'hidden')}>
            <h2 className="text-sm text-jeket-silver-muted">Programme</h2>
            <div className="aspect-video rounded-lg overflow-hidden border border-jeket-gold/10 bg-jeket-black-elevated flex items-center justify-center">
              <canvas ref={session.returnCanvas} className="w-full h-full object-contain" data-testid="return-feed" />
            </div>
            {admitted && stats && !stats.bidirectional && (
              <p className="text-xs text-jeket-silver-muted">Return feed not available on this connection yet.</p>
            )}
          </section>

          <section className="space-y-2">
            <h2 className="text-sm text-jeket-silver-muted">{admitted ? 'You' : 'Camera preview'}</h2>
            <div className="aspect-video rounded-lg overflow-hidden border border-jeket-gold/10">
              <Preview stream={session.preview} mirrored />
            </div>
            <div className="flex gap-2">
              <button
                onClick={session.toggleMic}
                disabled={session.audioState === 'unsupported' || session.audioState === 'no-mic'}
                aria-label={session.micMuted ? 'Unmute microphone' : 'Mute microphone'}
                className="p-2 rounded bg-jeket-black-elevated border border-jeket-gold/20 disabled:opacity-40"
              >
                {session.micMuted || session.audioState === 'unsupported' ? <MicOff size={16} /> : <Mic size={16} />}
              </button>
              <button
                onClick={session.toggleCamera}
                aria-label={session.cameraOff ? 'Turn camera on' : 'Turn camera off'}
                className="p-2 rounded bg-jeket-black-elevated border border-jeket-gold/20"
              >
                {session.cameraOff ? <VideoOff size={16} /> : <Video size={16} />}
              </button>
              {(admitted || join.phase === 'waiting' || join.phase === 'notice') && (
                <button
                  onClick={session.leave}
                  className="ml-auto flex items-center gap-2 px-3 py-2 rounded bg-red-600/80 text-white text-sm"
                >
                  <PhoneOff size={16} /> Leave
                </button>
              )}
            </div>
          </section>

          {!admitted && (
            <aside className="space-y-4">
              {join.phase === 'ready' && (
                <>
                  <label className="block text-sm space-y-1">
                    <span className="text-jeket-silver-muted">Camera</span>
                    <select
                      value={session.cameraId}
                      onChange={(e) => session.setCameraId(e.target.value)}
                      className="w-full bg-jeket-black-elevated border border-jeket-gold/20 rounded px-2 py-1.5"
                    >
                      <option value="">Default</option>
                      {session.devices.cameras.map((d) => (
                        <option key={d.deviceId} value={d.deviceId}>
                          {d.label || 'Camera'}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm space-y-1">
                    <span className="text-jeket-silver-muted">Microphone</span>
                    <select
                      value={session.micId}
                      onChange={(e) => session.setMicId(e.target.value)}
                      className="w-full bg-jeket-black-elevated border border-jeket-gold/20 rounded px-2 py-1.5"
                    >
                      <option value="">Default</option>
                      {session.devices.mics.map((d) => (
                        <option key={d.deviceId} value={d.deviceId}>
                          {d.label || 'Microphone'}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm space-y-1">
                    <span className="text-jeket-silver-muted">Your name</span>
                    <input
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      maxLength={80}
                      className="w-full bg-jeket-black-elevated border border-jeket-gold/20 rounded px-2 py-1.5"
                    />
                  </label>
                  {event.recordParticipantIsos && <RecordingNotice session={session} blocking={false} />}
                  <button
                    onClick={() => session.requestJoin(displayName.trim())}
                    disabled={!canJoin}
                    className="w-full py-2 rounded bg-jeket-gold text-jeket-black font-semibold disabled:opacity-40"
                  >
                    Ask to join
                  </button>
                </>
              )}
              {(join.phase === 'requesting' || join.phase === 'waiting') && (
                <p className="text-sm text-jeket-silver-muted" role="status">
                  Waiting for the producer to let you in…
                </p>
              )}
              {join.phase === 'notice' && <RecordingNotice session={session} blocking />}
              {join.phase === 'ended' ? (
                <div role="status" data-testid="session-ended" className="rounded-lg border border-jeket-gold/20 p-4 space-y-1">
                  <p className="font-semibold">Your session has ended</p>
                  <p className="text-sm text-jeket-silver-muted">{join.error ?? "You've left the event."}</p>
                </div>
              ) : (
                join.error && <p className="text-sm text-red-400">{join.error}</p>
              )}
            </aside>
          )}
        </div>

        {admitted && connection === 'reconnecting' && (
          <p className="text-sm text-jeket-gold" role="status">
            Connection lost — reconnecting. Stay on this page.
            {stats?.lastError ? ` (${stats.lastError})` : ''}
          </p>
        )}
      </main>
    </div>
  );
}
